/**
 * #2088, one Analyst conversation for the whole app.
 *
 * Phase 0 contract. `useChat()` holds messages, conversationId, the SSE stream
 * and the AbortController in component-local state, so a second caller gets a
 * second, empty conversation and a second stream. The panel and the /analyst
 * page must be two MOUNTS of one conversation, so the hook is instantiated
 * exactly once here, in AppShell, and both surfaces read it through context.
 *
 * Two things moved in with the state and must stay here:
 *  - the #769 busy flag, which used to be cleared when DataAnalyst unmounted.
 *    Leaving /analyst mid-answer would have cleared it while the panel was
 *    still streaming, and the shell would auto-reload over a running answer.
 *  - the AbortController inside useChat, so navigating away no longer aborts a
 *    stream the panel is showing.
 *
 * #2307: the panel's open flag survives a hard reload (localStorage) but
 * useChat's messages are component state, gone on remount, so a reload used
 * to greet the user with "New conversation" where their thread was a second
 * ago. The active conversation id now rides in the same per-user prefs slot;
 * on mount with a stored id this rehydrates it through the same
 * /api/conversations/:id read the full page and "Open in Data Analyst"
 * already use (useChat's loadConversation). A missing or forbidden id (404,
 * 403, a dropped connection) clears the stored id and falls back to a new
 * conversation rather than showing an error for a thread the user never
 * asked to open. All of this lives here so the panel component stays exactly
 * as it was: it just reads useAnalyst()/useAnalystPanel().
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from "react";
import { useChat } from "@/hooks/use-chat";
import { setChatBusy } from "@/lib/busy-signal";
import {
  DEFAULT_PANEL_PREFS,
  clampPanelWidth,
  readPanelPrefs,
  writePanelPrefs,
  type AnalystPanelPrefs,
} from "@/lib/analyst-panel-prefs";

type Chat = ReturnType<typeof useChat>;

export interface AnalystPanelState {
  open: boolean;
  width: number;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setWidth: (px: number) => void;
}

const ChatContext = createContext<Chat | null>(null);
const PanelContext = createContext<AnalystPanelState | null>(null);

export function AnalystProvider({ userId, children }: { userId?: string | null; children: ReactNode }) {
  const chat = useChat();

  // #769: the shell's version checker reads this imperatively to decide
  // reload-vs-toast. It follows the conversation now, not the page.
  useEffect(() => {
    setChatBusy(chat.isLoading || chat.streamingMessage !== null);
    return () => setChatBusy(false);
  }, [chat.isLoading, chat.streamingMessage]);

  const [prefs, setPrefs] = useState<AnalystPanelPrefs>(DEFAULT_PANEL_PREFS);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const lastUser = useRef<string | null | undefined>(undefined);
  // #2307: set while a stored conversation id is being rehydrated, so the
  // id-sync effect below does not write the still-null conversationId over
  // the very id it is trying to load.
  const rehydratingRef = useRef(false);

  const { loadConversation } = chat;

  // Hydrate once per user. Re-running on every render would fight the user's
  // own toggle; keying on the id also swaps state cleanly at a shared terminal.
  const storageId = userId ?? null;
  useEffect(() => {
    const key = storageId ?? "anon";
    if (hydratedFor === key) return;
    const stored = readPanelPrefs(storageId);
    setPrefs(stored);
    setHydratedFor(key);
    if (stored.conversationId) {
      rehydratingRef.current = true;
      loadConversation(stored.conversationId)
        .catch(() => {
          // Gone, not this user's, or the network dropped: clear the stale id
          // so the next mount does not try again, and settle on a new
          // conversation rather than surfacing the read's error.
          chat.clearChat();
          writePanelPrefs(storageId, { ...stored, conversationId: null });
          setPrefs((p) => ({ ...p, conversationId: null }));
        })
        .finally(() => {
          rehydratingRef.current = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageId, hydratedFor, loadConversation]);

  // Keep the stored conversation id in step with the live one: a new
  // conversation created by the first send, a switch to a different one, or
  // "Start a new conversation" clearing it back to null. Runs after this
  // user's prefs are hydrated, and sits out a rehydrate in flight above.
  useEffect(() => {
    if (hydratedFor !== (storageId ?? "anon")) return;
    if (rehydratingRef.current) return;
    setPrefs((p) => {
      if (p.conversationId === chat.conversationId) return p;
      const next = { ...p, conversationId: chat.conversationId };
      writePanelPrefs(storageId, next);
      return next;
    });
  }, [chat.conversationId, storageId, hydratedFor]);

  // The provider outlives a session flip, so without this the previous user's
  // transcript would still be sitting in the panel for the next one. clearChat
  // also aborts anything still streaming.
  const { clearChat } = chat;
  useEffect(() => {
    if (lastUser.current === undefined) {
      lastUser.current = storageId;
      return;
    }
    if (lastUser.current === storageId) return;
    lastUser.current = storageId;
    clearChat();
  }, [storageId, clearChat]);

  const persist = useCallback(
    (next: AnalystPanelPrefs) => {
      setPrefs(next);
      writePanelPrefs(storageId, next);
    },
    [storageId],
  );

  const panel = useMemo<AnalystPanelState>(
    () => ({
      open: prefs.open,
      width: prefs.width,
      openPanel: () => persist({ ...prefs, open: true }),
      closePanel: () => persist({ ...prefs, open: false }),
      togglePanel: () => persist({ ...prefs, open: !prefs.open }),
      setWidth: (px: number) => persist({ ...prefs, width: clampPanelWidth(px) }),
    }),
    [prefs, persist],
  );

  return (
    <ChatContext.Provider value={chat}>
      <PanelContext.Provider value={panel}>{children}</PanelContext.Provider>
    </ChatContext.Provider>
  );
}

/**
 * The one conversation. Calling `useChat()` directly from a component starts a
 * second one, which is the drift this provider exists to prevent.
 */
export function useAnalyst(): Chat {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useAnalyst needs an <AnalystProvider> (mounted in AppShell)");
  return ctx;
}

export function useAnalystPanel(): AnalystPanelState {
  const ctx = useContext(PanelContext);
  if (!ctx) throw new Error("useAnalystPanel needs an <AnalystProvider> (mounted in AppShell)");
  return ctx;
}
